import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';
import MergeChatLogo from '../components/MergeChatLogo';

function fmt(n) { return Number(n || 0).toLocaleString(); }
function fmtMin(min) {
  if (!min && min !== 0) return '—';
  const m = Number(min);
  if (m < 60) return `${m} นาที`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m % 60);
  return rem > 0 ? `${h}ชม ${rem}น` : `${h} ชม`;
}
function fmtHr(min) {
  if (!min && min !== 0) return '—';
  return (Number(min) / 60).toFixed(1) + ' ชม';
}
function score(agent) {
  const c = Number(agent.cases_closed || 0);
  const r = Number(agent.avg_response_min || 999);
  const m = Number(agent.messages_sent || 0);
  return c * 100 + m * 2 + (r < 15 ? 50 : r < 30 ? 25 : 0);
}

function exportCSV(agents, days) {
  const headers = ['ชื่อ Agent', 'Email', 'ข้อความที่ส่ง', 'สนทนาที่ handle', 'เคสที่ปิด', 'Avg. ตอบแรก (นาที)', 'Avg. ปิดเคส (ชม)'];
  const rows = agents.map(a => [
    a.name, a.email,
    fmt(a.messages_sent),
    fmt(a.conversations_handled),
    fmt(a.cases_closed),
    a.avg_response_min ?? '—',
    a.avg_resolution_min ? (Number(a.avg_resolution_min) / 60).toFixed(1) : '—',
  ]);
  const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `report_${days}d_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function RankBadge({ rank }) {
  if (rank === 1) return <span className="rank-badge rank-1">🥇</span>;
  if (rank === 2) return <span className="rank-badge rank-2">🥈</span>;
  if (rank === 3) return <span className="rank-badge rank-3">🥉</span>;
  return <span className="rank-badge">{rank}</span>;
}

function ResponseBar({ value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="report-bar-wrap">
      <div className="report-bar" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function ReportsPage() {
  const navigate  = useNavigate();
  const { agent, logout } = useAuth();
  const [days, setDays]   = useState(30);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('cases_closed');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(() => {
    setLoading(true);
    api.getAgentReport(days)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  const agents = data?.agents || [];
  const sorted = [...agents].sort((a, b) => {
    const av = Number(a[sortKey] || 0);
    const bv = Number(b[sortKey] || 0);
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  // Ranked by overall score for leaderboard
  const ranked = [...agents]
    .filter(a => Number(a.messages_sent) > 0 || Number(a.cases_closed) > 0)
    .sort((a, b) => score(b) - score(a));

  const maxMsgs = Math.max(...agents.map(a => Number(a.messages_sent)), 1);
  const maxResp = Math.max(...agents.filter(a => a.avg_response_min).map(a => Number(a.avg_response_min)), 1);
  const totalClosed = agents.reduce((s, a) => s + Number(a.cases_closed || 0), 0);
  const totalMsgs   = agents.reduce((s, a) => s + Number(a.messages_sent || 0), 0);

  function SortIcon({ k }) {
    if (sortKey !== k) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon active">{sortDir === 'desc' ? '↓' : '↑'}</span>;
  }

  return (
    <div className="dash-layout">
      {/* Sidebar */}
      <aside className="settings-sidebar">
        <div className="settings-logo">
          <MergeChatLogo size={32} />
          <span><span>Merge</span><span style={{ color: '#FF6347', fontWeight: 700 }}>Chat</span></span>
        </div>
        <nav className="settings-nav">
          <button className="nav-item" onClick={() => navigate('/')}>💬 กล่องข้อความ</button>
          <button className="nav-item" onClick={() => navigate('/dashboard')}>📊 Dashboard</button>
          <button className="nav-item active">📋 Reports</button>
          <button className="nav-item" onClick={() => navigate('/settings')}>⚙️ ตั้งค่า</button>
        </nav>
        <div className="settings-agent">
          <div className="agent-info">
            <div className="agent-avatar">{agent?.name?.slice(0, 2)}</div>
            <div>
              <div className="agent-name">{agent?.name}</div>
              <div className="agent-role">{agent?.role}</div>
            </div>
          </div>
          <button className="btn-logout-sm" onClick={logout}>ออก</button>
        </div>
      </aside>

      {/* Main */}
      <main className="dash-main">
        {/* Header */}
        <div className="dash-header">
          <div>
            <h1 className="dash-title">Reports</h1>
            <p className="dash-subtitle">Agent Performance — วิเคราะห์ประสิทธิภาพทีม</p>
          </div>
          <div className="dash-controls">
            {[7, 30, 90].map(d => (
              <button key={d} className={`dash-period ${days === d ? 'active' : ''}`} onClick={() => setDays(d)}>
                {d} วัน
              </button>
            ))}
            <button className="dash-refresh" onClick={load} title="Refresh">↻</button>
            {!loading && data && (
              <button className="btn-export" onClick={() => exportCSV(agents, days)}>
                ⬇ Export CSV
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dash-loading">กำลังโหลด...</div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="dash-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
              <div className="dash-stat" style={{ borderTop: '3px solid var(--accent)' }}>
                <div className="dash-stat-top"><span className="dash-stat-icon">✅</span></div>
                <div className="dash-stat-value">{fmt(totalClosed)}</div>
                <div className="dash-stat-label">เคสที่ปิดแล้ว ({days} วัน)</div>
              </div>
              <div className="dash-stat" style={{ borderTop: '3px solid #5B8DEF' }}>
                <div className="dash-stat-top"><span className="dash-stat-icon">📤</span></div>
                <div className="dash-stat-value">{fmt(totalMsgs)}</div>
                <div className="dash-stat-label">ข้อความที่ส่งทั้งหมด</div>
              </div>
              <div className="dash-stat" style={{ borderTop: '3px solid #FFB347' }}>
                <div className="dash-stat-top"><span className="dash-stat-icon">👥</span></div>
                <div className="dash-stat-value">{ranked.length}</div>
                <div className="dash-stat-label">Agent ที่ active</div>
              </div>
            </div>

            {/* Leaderboard */}
            {ranked.length > 0 && (
              <div className="dash-card" style={{ marginBottom: 16 }}>
                <div className="dash-card-title">🏆 Leaderboard — {days} วันที่ผ่านมา</div>
                <div className="leaderboard">
                  {ranked.map((a, i) => (
                    <div key={a.id} className={`lb-row ${i < 3 ? 'lb-top' : ''}`}>
                      <RankBadge rank={i + 1} />
                      <div className="lb-avatar">{a.name.slice(0, 2)}</div>
                      <div className="lb-info">
                        <div className="lb-name">{a.name}</div>
                        <div className="lb-meta">
                          ปิด {fmt(a.cases_closed)} เคส · {fmt(a.messages_sent)} ข้อความ
                          {a.avg_response_min && ` · ตอบแรก ${fmtMin(a.avg_response_min)}`}
                        </div>
                      </div>
                      <ResponseBar value={Number(a.messages_sent)} max={maxMsgs} color={i === 0 ? '#FFB347' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : 'var(--accent-mid)'} />
                      <div className="lb-msgs">{fmt(a.messages_sent)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detail table */}
            <div className="dash-card">
              <div className="dash-card-title">📋 รายละเอียดรายบุคคล</div>
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th className="sortable" onClick={() => handleSort('messages_sent')}>
                        ข้อความส่ง <SortIcon k="messages_sent" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('conversations_handled')}>
                        สนทนา <SortIcon k="conversations_handled" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('cases_closed')}>
                        ปิดเคส <SortIcon k="cases_closed" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('avg_response_min')}>
                        Avg. ตอบแรก <SortIcon k="avg_response_min" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('avg_resolution_min')}>
                        Avg. ปิดเคส <SortIcon k="avg_resolution_min" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('avg_msg_per_case')} title="เฉลี่ยข้อความที่ส่งต่อ 1 เคส — ต่ำผิดปกติอาจรับเคสแล้วไม่ทำ">
                        ข้อความ/เคส <SortIcon k="avg_msg_per_case" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('quick_close_count')} title="จำนวนเคสที่ปิดภายใน 2 นาทีหลังตอบครั้งแรก">
                        ปิดเร็ว ⚡ <SortIcon k="quick_close_count" />
                      </th>
                      <th>Msg Bar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map(a => {
                      const respColor = !a.avg_response_min ? '#94a3b8'
                        : Number(a.avg_response_min) <= 15 ? 'var(--accent)'
                        : Number(a.avg_response_min) <= 30 ? '#FFB347'
                        : 'var(--red)';
                      return (
                        <tr key={a.id}>
                          <td>
                            <div className="report-agent-cell">
                              <div className="report-avatar">{a.name.slice(0, 2)}</div>
                              <div>
                                <div className="report-name">{a.name}</div>
                                <div className="report-email">{a.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="num-cell">{fmt(a.messages_sent)}</td>
                          <td className="num-cell">{fmt(a.conversations_handled)}</td>
                          <td className="num-cell">
                            <span className="cases-badge">{fmt(a.cases_closed)}</span>
                          </td>
                          <td className="num-cell" style={{ color: respColor, fontWeight: 600 }}>
                            {fmtMin(a.avg_response_min)}
                          </td>
                          <td className="num-cell">{fmtHr(a.avg_resolution_min)}</td>
                          <td className="num-cell" style={{ color: Number(a.avg_msg_per_case) > 0 && Number(a.avg_msg_per_case) < 2 ? 'var(--orange)' : undefined, fontWeight: Number(a.avg_msg_per_case) < 2 ? 600 : undefined }}>
                            {a.avg_msg_per_case ?? '—'}
                          </td>
                          <td className="num-cell" style={{ color: Number(a.quick_close_count) > 0 ? 'var(--orange)' : undefined, fontWeight: Number(a.quick_close_count) > 0 ? 600 : undefined }}>
                            {Number(a.quick_close_count) > 0 ? `⚡ ${a.quick_close_count}` : '—'}
                          </td>
                          <td>
                            <ResponseBar value={Number(a.messages_sent)} max={maxMsgs} color="var(--accent)" />
                          </td>
                        </tr>
                      );
                    })}
                    {sorted.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>ไม่มีข้อมูลในช่วงเวลานี้</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div className="report-legend">
                <span className="legend-dot" style={{ background: 'var(--accent)' }} /> ตอบแรก ≤ 15 นาที (ดี)
                <span className="legend-dot" style={{ background: '#FFB347', marginLeft: 12 }} /> 16–30 นาที (ปานกลาง)
                <span className="legend-dot" style={{ background: 'var(--red)', marginLeft: 12 }} /> &gt; 30 นาที (ต้องปรับปรุง)
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
